import { supabase } from './supabaseClient.js';

/**
 * Channel Context System
 *
 * Pulls user-written channel messages into AI context so that anything
 * written in the app's channels feeds into the RP universe knowledge.
 * Works alongside discordContext.js (which handles imported Discord history).
 *
 * This ensures the AI "knows" about conversations, worldbuilding, and
 * lore that users have created directly in the MFML app.
 */

// ─── CACHE ─────────────────────────────────────────────────────────────────
// Simple in-memory cache to avoid re-fetching the same data within a session.
// Key: cacheKey string, Value: { data, timestamp }
const _cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached(key) {
  const entry = _cache.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) return entry.data;
  _cache.delete(key);
  return null;
}

function setCache(key, data) {
  _cache.set(key, { data, timestamp: Date.now() });
  // Prevent unbounded growth
  if (_cache.size > 50) {
    const oldest = _cache.keys().next().value;
    _cache.delete(oldest);
  }
}

// ─── CHARACTER-SPECIFIC CHANNEL CONTEXT ────────────────────────────────────

/**
 * Fetches channel messages that mention or are relevant to a specific character.
 * This gives the AI knowledge of what users have discussed about a character
 * in the app's own channels.
 *
 * @param {string} characterName - The character name to search for
 * @param {number} limit - Max snippets to return (default: 8)
 * @returns {Promise<Array<{channel: string, author: string, text: string}>>}
 */
export async function getCharacterChannelContext(characterName, limit = 8) {
  if (!characterName || typeof characterName !== 'string') return [];

  const cacheKey = `char_channel_${characterName.toLowerCase()}_${limit}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const firstName = characterName.split(' ')[0].trim();

    // Search for messages mentioning this character
    const { data, error } = await supabase
      .from('channel_messages')
      .select(`
        content,
        user_id,
        channel_id,
        created_at,
        channels!inner(name)
      `)
      .ilike('content', `%${firstName}%`)
      .gt('content', '')
      .order('created_at', { ascending: false })
      .limit(limit * 4); // Fetch extra for filtering

    if (error) {
      console.error('Channel context fetch error:', error);
      return [];
    }

    if (!data || data.length === 0) return [];

    // Filter for substantial messages (skip short reactions)
    const substantial = data.filter(msg => msg.content && msg.content.length > 40);

    // Shuffle for variety
    // Deterministic stride-sample (audit P3-9): the shuffle made every
    // regeneration non-reproducible. A stride over the recency-ordered
    // pool keeps the variety without the randomness.
    {
      const stride = Math.max(1, Math.floor(substantial.length / Math.max(1, Math.min(substantial.length, 24))));
      const strided = substantial.filter((_, i) => i % stride === 0);
      substantial.length = 0;
      substantial.push(...strided);
    }

    const snippets = substantial.slice(0, limit).map(msg => ({
      channel: msg.channels?.name || 'channel',
      text: msg.content.substring(0, 400),
    }));

    setCache(cacheKey, snippets);
    return snippets;
  } catch (err) {
    console.error('Error fetching character channel context:', err);
    return [];
  }
}

// ─── UNIVERSE CHANNEL CONTEXT ──────────────────────────────────────────────

/**
 * Fetches recent channel messages that are RP-relevant — worldbuilding,
 * character discussions, lore, and story planning. This gives the AI
 * general universe knowledge from the app's own channels.
 *
 * @param {Array<string>} characterNames - Names of characters in the current scene
 * @param {number} limit - Max snippets to return (default: 10)
 * @returns {Promise<Array<{channel: string, text: string}>>}
 */
export async function getUniverseChannelContext(characterNames = [], limit = 10) {
  const cacheKey = `universe_channel_${(characterNames || []).sort().join('_')}_${limit}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    // Fetch recent substantial messages across all channels
    const { data, error } = await supabase
      .from('channel_messages')
      .select(`
        content,
        user_id,
        channel_id,
        created_at,
        channels!inner(name)
      `)
      .gt('content', '')
      .order('created_at', { ascending: false })
      .limit(200);

    if (error || !data || data.length === 0) return [];

    // Score messages by RP relevance
    const rpKeywords = /character|species|vampire|werewolf|siren|fae|demon|god|pack|kingdom|court|magic|power|bond|mate|alpha|beta|council|throne|clan|route|story|timeline|au |backstory|lore|plot/i;
    const namePattern = characterNames.length > 0
      ? new RegExp(characterNames.map(n => n.split(' ')[0]).join('|'), 'i')
      : null;

    const scored = data
      .filter(msg => msg.content && msg.content.length > 50)
      .map(msg => {
        let score = 0;
        if (rpKeywords.test(msg.content)) score += 2;
        if (namePattern && namePattern.test(msg.content)) score += 3;
        if (msg.content.length > 150) score += 1; // Longer = more context
        return { ...msg, score };
      })
      .filter(msg => msg.score > 0)
      .sort((a, b) => b.score - a.score);

    // Take top-scoring messages, then shuffle for variety
    const top = scored.slice(0, limit * 2);
    // Deterministic stride-sample (audit P3-9): the shuffle made every
    // regeneration non-reproducible. A stride over the recency-ordered
    // pool keeps the variety without the randomness.
    {
      const stride = Math.max(1, Math.floor(top.length / Math.max(1, Math.min(top.length, 24))));
      const strided = top.filter((_, i) => i % stride === 0);
      top.length = 0;
      top.push(...strided);
    }

    const snippets = top.slice(0, limit).map(msg => ({
      channel: msg.channels?.name || 'channel',
      text: msg.content.substring(0, 400),
    }));

    setCache(cacheKey, snippets);
    return snippets;
  } catch (err) {
    console.error('Error fetching universe channel context:', err);
    return [];
  }
}
